import React from 'react';
import { Link } from '../../../spa/Components/Link/Link';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import PropTypes from 'prop-types';
import flow from 'lodash/flow';
import classNames from 'classnames';

import { getTeamLogoUrl } from '~shared/utils/sportsbook';
import { showWidget } from '../../../shared/utils/widgets.js';
import WidgetContainer from '../../../shared/ui/WidgetContainer';
import {
    COMPONENT_CLASS,
    POPULAR_TITLE,
    POPULAR_TITLE_ICON_CLASS_NAME,
    POPULAR_EVENT_GTM_TAG,
}
from '../constants';
import EventSchedule from '../../../shared/components/react/EventSchedule';
import i18n from '../../../shared/utils/i18n';
import { getHasBoostedOddsSelections, getTournamentSportLogoUrl } from '~shared/utils/sportsbook';

import * as popularWidgetActions from '../actions/popularWidgetActions';
import { seoUrlEvent, seoUrlInplayEvent } from '../../../spa/Utils/SeoUrls.js';
import { withLazyLoadContext } from '~spa/Components/LazyLoad/LazyLoad';

export class PopularWidgetContainer extends React.Component {

    constructor(props) {
        super(props);
        this.wsEventListener = this.wsEventListener.bind(this);
    }

    static propTypes = {
        events: PropTypes.array,
        getPopularWidgetEvents: PropTypes.func,
        commonDataEvents: PropTypes.object,
        updateCommonStoreWsData: PropTypes.func,
        stopListeningForUpdates: PropTypes.func,
        source: PropTypes.string,
        lazyLoadHasLoaded: PropTypes.func,
    }

    static defaultProps = {
        events: [],
        getPopularWidgetEvents: () => null,
        commonDataEvents: {},
        updateCommonStoreWsData: () => null,
        stopListeningForUpdates:() => null,
        source: '',
        lazyLoadHasLoaded: () => null,
    }

    /**
     * After init DOM component, load page data
     * @returns {void}
     */
    componentDidMount() {
        this.refreshPopularWidget(true);
     }

     componentWillUnmount() {
        this.props.stopListeningForUpdates();
     }

     async refreshPopularWidget(fromBE) {

         if (fromBE) {
             await this.props.getPopularWidgetEvents(this.wsEventListener);
         } else {
             const waitMs = 500;
             await new Promise(resolve => setTimeout(() => {
                 resolve();
             }, waitMs));
         }

         this.props.lazyLoadHasLoaded();
     }

      /**
     * Event Listener for WS updates.
     * @param {Object} wsData Object of events to be updated inside Common Store
     * @return {void}
     */
    wsEventListener(wsData) {
        this.props.updateCommonStoreWsData(wsData);
    }

    /**
     * Returns event name with team logos
     * @param {String} sportSlug sport slug
     * @param {String} categorySlug category slug
     * @param {Object} competitors competitors object
     * @param {Boolean} isUsFormat isUsFormat
     * @returns {JSX} Returns a React Element
     */
    generateEventNameWithLogo(sportSlug, categorySlug, competitors, isUsFormat) {
        const competitorNames = Object.values(competitors).sort((a, b) => a.order - b.order);
        if (isUsFormat) {
            competitorNames.reverse();
        }

        return competitorNames.reduce((acc, current, index) => {
            const { slug: teamSlug, name } = current;
            const logoUrl = getTeamLogoUrl(sportSlug, categorySlug, teamSlug);
            const teamLogo = <img src={logoUrl} key={index}/>;
            const teamName = index === 0
                ? `${name} ${isUsFormat ? '@' : 'v'}`
                : name;

            return [
                ...acc,
                teamLogo,
                teamName,
            ];
        }, []);
    }

    /**
     * Returns an event template.
     * @param {Object} event Event object
     * @param {Number} key For DOM Element key property
     * @returns {String} HTML tag
     */
    createEventTemplate({
        name,
        event_url,
        isUsFormat,
        competitors,
        id: eventId,
        inplay: isInplay,
        start_time: startTime,
        sport_slug: sportSlug,
        hasBoostedOddsSelections,
        category_slug: categorySlug,
        tournament_slug: tournamentSlug,
        tournament_logos: tournamentLogos,
        is_streaming_available: isStreamingAvailable = false,
        each_way_plus: isEachWayPlus = false,
    }, key) {
        let eventUrl;

        if (isInplay && (sportSlug !== 'golf')) {
            eventUrl = seoUrlInplayEvent(event_url, eventId);
        } else {
            eventUrl = seoUrlEvent(event_url, eventId);
        }

        const gtm = `${this.props.source};${POPULAR_EVENT_GTM_TAG};${name}`;
        const showTournamentSportLogo = tournamentLogos && tournamentLogos.showTournamentSportLogo;
        const iconClass = classNames({
            'bb-icon': true,
            'bb-icon--size-24x24': true,
            [`bb-icon--${sportSlug}`]: !showTournamentSportLogo,
        });
        let customTournamentSportLogoStyle = null;

        if (showTournamentSportLogo) {
            customTournamentSportLogoStyle = {
                backgroundImage: `url(${getTournamentSportLogoUrl(sportSlug, categorySlug, tournamentSlug)})`,
            };
        }

        const streamingIconClasses = () => classNames('c-popular-events__icons-streaming', {
            ['c-popular-events__icons-streaming--inplay']: isInplay,
        });

        return (
            <div key={key} className='c-popular-events'>
                <Link className='c-popular-events__link' to={eventUrl} gtmAttribute={gtm}>
                    <div className={iconClass} style={customTournamentSportLogoStyle} />
                    <div className='c-popular-events__date'>
                        <EventSchedule
                            startTime={startTime}
                            isInplay={isInplay} />
                    </div>
                    <div className='c-popular-events__names'>
                        {
                            tournamentLogos && tournamentLogos.showTeamLogo
                            ? this.generateEventNameWithLogo(sportSlug, categorySlug, competitors, isUsFormat)
                            : name
                        }
                    </div>
                    <div className='c-popular-events__icons'>
                        { isEachWayPlus && <span className='c-popular-events__icons-ew-plus'>{i18n.t('racing.ew_plus')}</span>}
                        { hasBoostedOddsSelections && <span className='c-popular-events__icons-boosted-odds' /> }
                        { isStreamingAvailable && <span className={streamingIconClasses()} /> }
                    </div>
                </Link>
            </div>
        );
    }

      /**
     * Render the component
     * @returns {JSX.Element} Returns a React Element
     */
    render() {
        return (
            <div className={COMPONENT_CLASS}>
                {showWidget(this.props.events, this.props.commonDataEvents) && <WidgetContainer
                    title={i18n.t(POPULAR_TITLE)}
                    titleIconClassName={POPULAR_TITLE_ICON_CLASS_NAME}
                >
                    {Object.keys(this.props.events).map(
                        (key) => {
                                const {
                                    event_id: eventId,
                                    market_id: marketId,
                                } = this.props.events[key];
                                const eventObj = this.props.commonDataEvents[eventId];
                                if (!eventObj) {
                                    // eventObj is null when event changes from preplay to inplay.
                                    // This is a temporary fix to stop the client crashing.
                                    // There will be a further investigation into why there is a discrepency
                                    // between the events in the 'events' and 'commonDataEvents' obejcts.
                                    // @todo BT-9170
                                    return '';
                                }

                                const { selections } = eventObj.markets[marketId] || {};
                                const hasBoostedOddsSelections = getHasBoostedOddsSelections(selections);

                                const event = {
                                    ...this.props.commonDataEvents[eventId],
                                    hasBoostedOddsSelections,
                                };

                                return typeof event !== 'undefined' ?
                                    this.createEventTemplate(event, key) : '';
                            })
                    }
                </WidgetContainer>}
            </div>
        );
    }
}

/**
 * React-redux bindings
 * @param {*} state the current state
 * @returns {object} props
 */
function mapStateToProps(state) {
    return {
        events: state.popularWidget.events,
        commonDataEvents: state.commonData,
    };
}

/**
 * React-redux bindings
 * @param {*} dispatch the dispatcher function
 * @returns {object} betfeed actions
 */
function mapDispatchToProps(dispatch) {
    return bindActionCreators(popularWidgetActions, dispatch);
}

export default flow(
    withLazyLoadContext(),
    connect(mapStateToProps, mapDispatchToProps),
)(PopularWidgetContainer);
